import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OrderMeshLogo } from '@components/composition/OrderMeshLogo';
import { ForbiddenPlaceholder } from '@components/composition/ForbiddenPlaceholder';
import { isPermissionDenied } from '@/lib/apiError';
import { useAuth, useIsAuthenticated } from '@hooks/auth';
import { useLazyGetMerchantByIdQuery } from '@features/merchant/api';
import { useUpdateUserAttributesMutation } from '@features/users/api';
import {
  useValidateInvitationQuery,
  useGetPasswordPolicyQuery,
  useOnboardUserMutation,
  useLogoutMutation,
} from '../api';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { buildPasswordSchema } from '../lib/passwordPolicy';
import type { OnboardRequest } from '../types';

/**
 * Onboard (accept-invite) page. Direct port of legacy
 * `pages/Auth/Onboard/index.js` (the public `/onboard/:code` +
 * `/onboard/vendor/:code` routes that the Invite User "Send Invite Link" lands
 * on). On mount it validates the code and fetches the password policy; renders
 * the create-account form for a valid, unused code; on submit it onboards then
 * signs the user in and shows the success modal. Vendor vs merchant is decided
 * by the URL path, exactly as the legacy did.
 *
 * Field selection (org/parent name, invite + association copy) is ported
 * verbatim from the legacy IIFE. The legacy showed English literals (it was
 * never internationalized); we route the chrome through the `auth` namespace
 * (en only — es/fr/pt fall back to en, matching the legacy English-only page).
 */
export function OnboardPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { invitationCode = '' } = useParams();
  const isVendorOnboard = location.pathname.startsWith('/onboard/vendor/');

  const isAuthenticated = useIsAuthenticated();
  const { signIn } = useAuth();
  const [logout] = useLogoutMutation();

  // If a user arrives already signed in, wipe the session first so onboard
  // runs clean (legacy `performFullLogoutCleanup`). One-shot.
  const cleanupRef = useRef(isAuthenticated);
  const [cleaningUp, setCleaningUp] = useState(isAuthenticated);
  useEffect(() => {
    if (!cleanupRef.current) return;
    void logout()
      .unwrap()
      .catch(() => undefined)
      .finally(() => setCleaningUp(false));
  }, [logout]);

  const validate = useValidateInvitationQuery(
    { invitationCode, isVendor: isVendorOnboard },
    { skip: !invitationCode || cleaningUp },
  );
  const policyQuery = useGetPasswordPolicyQuery(undefined, { skip: cleaningUp });
  const [onboard, onboardState] = useOnboardUserMutation();
  const [fetchParentMerchant] = useLazyGetMerchantByIdQuery();
  const [updateUserAttributes] = useUpdateUserAttributesMutation();

  const data = validate.data;
  const policy = policyQuery.data;
  const isExistingUser = Boolean(data?.isExistingUser);
  const codeInvalid = validate.isError || (data && !data.isInvitationCodeValid);
  const showForm = Boolean(data?.isInvitationCodeValid && !isExistingUser);

  const [showPassword, setShowPassword] = useState(false);
  const [onboardError, setOnboardError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // ── Display copy (legacy field-selection IIFE, verbatim) ──
  const organizationName = isVendorOnboard
    ? data?.vendorSubaccountName || data?.vendorName
    : data?.merchantSubaccountName || data?.merchantName;
  const parentOrganizationName = isVendorOnboard
    ? data?.vendorSubaccountName
      ? data?.vendorName
      : null
    : data?.merchantSubaccountName
      ? data?.merchantName
      : null;
  const inviteText = parentOrganizationName
    ? t('onboard.inviteWithParent', { parent: parentOrganizationName, org: organizationName })
    : organizationName
      ? t('onboard.invite', { org: organizationName })
      : t('onboard.inviteGeneric');
  const associationText = organizationName
    ? t('onboard.associationWith', { org: organizationName })
    : t('onboard.associationGeneric');

  // ── Form ──
  const schema = useMemo(
    () =>
      z
        .object({
          firstName: isExistingUser ? z.string().optional() : z.string().min(1, t('onboard.errors.firstName')),
          lastName: isExistingUser ? z.string().optional() : z.string().min(1, t('onboard.errors.lastName')),
          email: z.string().min(1, t('onboard.errors.emailRequired')).email(t('onboard.errors.emailInvalid')),
          password: buildPasswordSchema(policy, t),
          passwordConfirmation: z.string().min(1, t('onboard.errors.confirmRequired')),
        })
        .refine((v) => v.password === v.passwordConfirmation, {
          path: ['passwordConfirmation'],
          message: t('onboard.errors.passwordsMatch'),
        }),
    [isExistingUser, policy, t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, watch, formState } = useForm<FormValues>({
    // zod 4 types the schema's input apart from its output — cast scoped to
    // the resolver only (repo convention, see CreateEditReasonModal).
    resolver: zodResolver(schema) as unknown as Resolver<FormValues>,
    defaultValues: { firstName: '', lastName: '', email: '', password: '', passwordConfirmation: '' },
  });

  // Prefill (+ lock) the email when the invitation pins a specific address.
  useEffect(() => {
    if (data?.email) reset((prev) => ({ ...prev, email: data.email ?? '' }));
  }, [data?.email, reset]);

  const passwordValue = watch('password');

  /**
   * Faithful port of the legacy `handleOnboardUser` saga block.
   * For a MERCHANT peer invite from a parent (`parentMerchantId` set, no
   * `childMerchantId`), associate the parent's existing subaccounts onto the
   * new user so they can see them. Best-effort: any failure is swallowed so it
   * never blocks onboarding — identical to the legacy try/catch. Runs after
   * onboard + before sign-in, matching the legacy ordering.
   */
  const applyPeerSubaccountAssociation = async (newUserId: string | undefined): Promise<void> => {
    if (isVendorOnboard || !newUserId) return;
    const parentMerchantId = data?.parentMerchantId;
    const isPeerInviteFromParent = Boolean(parentMerchantId) && !data?.childMerchantId;
    if (!isPeerInviteFromParent || !parentMerchantId) return;
    try {
      const parent = await fetchParentMerchant({ merchantId: parentMerchantId }).unwrap();
      const subaccountIds = (parent?.children ?? []).map((c) => c.id).filter((id): id is string => Boolean(id));
      await updateUserAttributes({
        userId: newUserId,
        type: subaccountIds.length > 0 ? 'parent' : '',
        entity: 'merchant',
        entityId: parentMerchantId,
        associatedIDs: subaccountIds,
      }).unwrap();
    } catch {
      // Swallow — the user is already onboarded; this association is a
      // best-effort convenience (legacy logs + ignores).
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setOnboardError(null);
    // Prefer the pinned-invite address; fall back to the typed value. RHF 7.76
    // does include the disabled (pinned) field in submit values, but deriving
    // from `data?.email` keeps this correct regardless of RHF's disabled-field
    // handling so a future upgrade can't silently break pinned onboarding.
    const email = (data?.email ?? values.email ?? '').trim();
    const payload: OnboardRequest = isExistingUser
      ? { email, password: values.password, invitationCode }
      : { email, firstName: values.firstName, lastName: values.lastName, password: values.password, invitationCode };
    try {
      const resp = await onboard({ data: payload, isVendor: isVendorOnboard }).unwrap();
      await applyPeerSubaccountAssociation(resp?.id);
      const ok = await signIn(email, values.password);
      if (ok) setShowSuccess(true);
      else setOnboardError(t('onboard.errors.signInAfter'));
    } catch (err) {
      const errData = (err as {
        data?: {
          errors?: { errorMessage?: string }[];
          Errors?: { ErrorMessage?: string }[];
        };
      })?.data;
      const serverMessage = errData?.errors?.[0]?.errorMessage ?? errData?.Errors?.[0]?.ErrorMessage;
      setOnboardError(serverMessage ?? t('onboard.errors.generic'));
    }
  });

  const onboardForbidden = onboardState.isError && isPermissionDenied(onboardState.error);
  const busy = cleaningUp || validate.isLoading || onboardState.isLoading;

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
      data-testid="onboard-page-root"
    >
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-6 inline-flex items-center"
          aria-label="OrderMesh"
          data-testid="onboard-logo"
        >
          <OrderMeshLogo className="h-4 w-auto text-foreground" role="img" />
        </button>

        <Card data-testid="onboard-header-card">
          <CardHeader>
            <CardTitle>{t('onboard.title')}</CardTitle>
            {busy && !showForm ? (
              <CardDescription className="flex items-center gap-2" data-testid="onboard-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> {t('onboard.loading')}
              </CardDescription>
            ) : showForm ? (
              <>
                <CardDescription data-testid="onboard-invite-text">{inviteText}</CardDescription>
                <CardDescription data-testid="onboard-association-text">{associationText}</CardDescription>
              </>
            ) : null}
          </CardHeader>

          {!busy && isExistingUser && (
            <CardContent className="flex flex-col gap-4">
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="onboard-existing-user-message"
              >
                {onboardError || t('onboard.alreadyUsed')}
              </div>
              <Button onClick={() => navigate('/signin')} data-testid="onboard-existing-user-back-button">
                {t('onboard.backToLogin')}
              </Button>
            </CardContent>
          )}

          {!busy && codeInvalid && !isExistingUser && (
            <CardContent className="flex flex-col gap-4">
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="onboard-invalid-code-message"
              >
                {onboardError || t('onboard.invalidCode')}
              </div>
              <Button onClick={() => navigate('/signin')} data-testid="onboard-invalid-code-back-button">
                {t('onboard.backToLogin')}
              </Button>
            </CardContent>
          )}
        </Card>

        {showForm && (
          <Card className="mt-4" data-testid="onboard-form-card">
            <CardContent className="pt-6">
              {onboardForbidden ? (
                <ForbiddenPlaceholder error={onboardState.error} variant="section" data-testid="onboard-forbidden" />
              ) : (
                <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
                  <Field id="onboard-first-name" label={t('onboard.firstName')} error={formState.errors.firstName?.message}>
                    <Input
                      id="onboard-first-name"
                      placeholder={t('onboard.firstName')}
                      data-testid="onboard-first-name-input"
                      {...register('firstName')}
                    />
                  </Field>
                  <Field id="onboard-last-name" label={t('onboard.lastName')} error={formState.errors.lastName?.message}>
                    <Input
                      id="onboard-last-name"
                      placeholder={t('onboard.lastName')}
                      data-testid="onboard-last-name-input"
                      {...register('lastName')}
                    />
                  </Field>
                  <Field id="onboard-email" label={t('onboard.email')} error={formState.errors.email?.message}>
                    <Input
                      id="onboard-email"
                      type="email"
                      autoComplete="email"
                      placeholder={t('onboard.email')}
                      disabled={Boolean(data?.email)}
                      data-testid="onboard-email-input"
                      {...register('email')}
                    />
                  </Field>
                  <Field id="onboard-password" label={t('onboard.password')} error={formState.errors.password?.message}>
                    <div className="relative">
                      <Input
                        id="onboard-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder={t('onboard.password')}
                        data-sentry-mask="true"
                        className="pr-10"
                        data-testid="onboard-password-input"
                        {...register('password')}
                      />
                      <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} t={t} />
                    </div>
                  </Field>
                  <PasswordRequirements password={passwordValue ?? ''} policy={policy} />
                  <Field
                    id="onboard-password-confirmation"
                    label={t('onboard.confirmPassword')}
                    error={formState.errors.passwordConfirmation?.message}
                  >
                    <div className="relative">
                      <Input
                        id="onboard-password-confirmation"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder={t('onboard.confirmPassword')}
                        data-sentry-mask="true"
                        className="pr-10"
                        data-testid="onboard-password-confirmation-input"
                        {...register('passwordConfirmation')}
                      />
                      <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} t={t} />
                    </div>
                  </Field>

                  {onboardError && (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                      data-testid="onboard-error"
                    >
                      {onboardError}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={busy} data-testid="onboard-submit-button">
                    {onboardState.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('onboard.signIn')}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Success — non-dismissable; the only exit is "Go to Dashboard" (legacy
          showCloseButton=false). */}
      <Dialog open={showSuccess} onOpenChange={() => undefined}>
        <DialogContent
          data-testid="onboard-success-modal"
          hideClose
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t('onboard.success.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {organizationName ? t('onboard.success.body', { name: organizationName }) : t('onboard.success.bodyGeneric')}
          </p>
          <DialogFooter>
            <Button onClick={() => navigate('/')} data-testid="onboard-success-dashboard-button">
              {t('onboard.success.cta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

/** Labeled field wrapper — matches the SignInForm field markup. */
function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Show/hide eye toggle — same idiom as SignInForm. */
function PasswordToggle({
  shown,
  onToggle,
  t,
}: {
  shown: boolean;
  onToggle: () => void;
  t: (k: string) => string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? t('signIn.hidePassword') : t('signIn.showPassword')}
      aria-pressed={shown}
      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      data-testid="onboard-password-toggle"
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );
}
